/**
 * The kinds of invoice the list and detail screens show. One list, one detail, switched by `kind`;
 * the server (V2Controller, _invoiceTables) picks the legacy tables and the menu right.
 */
export type Kind = "racuni" | "gotovinski" | "prejeti" | "masovni" | "ponudbe";

export interface KindInfo {
    key: Kind;
    /** issued by the company, or received from a supplier; the switch above the list groups by this */
    direction: "offer" | "issued" | "received";
    /** label of the switch above the list */
    tab: string;
    /** menu entries that give the user this kind (any one of them) */
    menu: string[];
    /** "N <noun>" in the list summary, and "Še nimate <noun>." when empty (genitive plural) */
    noun: string;
    /** what the other side of the invoice is called */
    partner: string;
    /** heading above the invoice number on its own screen */
    title: string;
    /** the number shown as an example in the number filter */
    numberExample: string;
    /** shown under "Še nimate ..." */
    emptyHint: string;
}

export const KINDS: KindInfo[] = [
    {
        key: "racuni", direction: "issued", tab: "Izdani računi", menu: ["racuni_izdaniRacuni", "knjizenje_izdaniRacuni"],
        noun: "izdanih računov", partner: "Stranka", title: "Izdani", numberExample: "0001-19",
        emptyHint: "Ko izdate prvega, se bo pojavil tukaj.",
    },
    {
        key: "gotovinski", direction: "issued", tab: "Gotovinski računi", menu: ["racuni_gotovinskiRacuni", "knjizenjeV2_gotovinskiRacuni"],
        noun: "gotovinskih računov", partner: "Stranka", title: "Gotovinski", numberExample: "PIS-BLA1-2 / 19",
        emptyHint: "Ko izdate prvega, se bo pojavil tukaj.",
    },
    {
        // Offers are issued by the company but never posted; whoever may open issued invoices may open them.
        key: "ponudbe", direction: "offer", tab: "Izdane ponudbe", menu: ["racuni_izdaniRacuni", "knjizenje_izdaniRacuni", "racuni_ponudbe_seznamIzdanihPonudb"],
        noun: "izdanih ponudb", partner: "Stranka", title: "Izdana", numberExample: "0001-19",
        emptyHint: "Ko izdate prvo, se bo pojavila tukaj.",
    },
    {
        key: "prejeti", direction: "received", tab: "Prejeti računi", menu: ["racuni_prejetiRacuni", "knjizenje_prejetiRacuni"],
        noun: "prejetih računov", partner: "Dobavitelj", title: "Prejeti", numberExample: "0262-20",
        emptyHint: "Ko vnesete prvega, se bo pojavil tukaj.",
    },
    {
        key: "masovni", direction: "issued", tab: "Iz masovne obdelave", menu: ["racuni_izdaniRacuniIzMasovneObdelave"],
        noun: "računov iz masovne obdelave", partner: "Stranka", title: "Izdani, iz masovne obdelave", numberExample: "0000000307203",
        emptyHint: "Pojavili se bodo po masovni obdelavi.",
    },
];

export const kindInfo = (k: Kind): KindInfo => KINDS.find(x => x.key === k) ?? KINDS[0];

/** Any value from a URL or a prop, as a known kind (issued invoices when unknown). */
export function kindOf(v: unknown): Kind {
    return KINDS.some(k => k.key === v) ? (v as Kind) : "racuni";
}

/** Every menu entry that gives any kind: the rule for the screens that show them all. */
export const ALL_KIND_MENUS: string[] = KINDS.flatMap(k => k.menu);
